import { ApiResponseError } from '@api/apiUtils/ApiResponseError';

/**
 * Имя тега, под которым причина транспорта уезжает в Sentry.
 */
export const TRANSPORT_TAG = 'transport';

/**
 * Достаёт причину, по которой запрос остался без ответа (AF-537).
 *
 * ## Зачем
 *
 * Ветка «ответа нет» в общем клиенте складывает в одно сообщение «Сервер не
 * отвечает» ЧЕТЫРЕ разные вещи: обрыв сети, таймаут, отвергнутый origin и
 * отменённый запрос. Разница между ними известна — axios кладёт её в
 * `error.code`, а клиент сохраняет в `ApiResponseError.type` (`ERR_NETWORK`,
 * `ECONNABORTED`, `ERR_CANCELED`, `ERR_BAD_REQUEST`). В Sentry она не попадала
 * вовсе: наружу уходило только имя класса и текст.
 *
 * Поэтому 41 событие в `flow-analist` сегодня нельзя разделить даже в
 * телеметрии — а решения для этих четырёх причин РАЗНЫЕ: обрыв у человека не
 * наш дефект, таймаут бэкенда наш, отвергнутый origin — вообще ошибка
 * конфигурации. Тег ставится раньше, чем подписи на экране, именно поэтому:
 * без него выбор подписи делался бы наугад.
 *
 * ## Почему тег, а не отдельный тип ошибки
 *
 * Разные классы ошибок разнесли бы группы в Sentry — и заодно сломали бы
 * `instanceof ApiResponseError` в приложении. Тег даёт разделение там, где оно
 * нужно (фильтр в Sentry), и ничего не меняет в коде, который ловит ошибку.
 *
 * @returns причину для тега или `undefined`, если ошибка не про транспорт —
 * тогда тег не ставится вовсе, и лишнего измерения не появляется.
 */
export const readTransportCause = (error: unknown): string | undefined => {
  if (!(error instanceof ApiResponseError)) {
    return undefined;
  }

  /*
   * Признак `noResponse`, а НЕ код 503.
   *
   * Клиент ставит себе 503, когда ответа не было вовсе, — но ровно такой же
   * код приходит и настоящим ответом сервера (`503 Service Unavailable`).
   * Проверка по числу пометила бы живой ответ сервера тегом «транспорт» и
   * испортила бы ровно тот замер, ради которого тег и заводится
   * (найдено adversarial-ревью AF-537).
   */
  if (!error.noResponse) {
    return undefined;
  }

  /*
   * `type` может отсутствовать: axios не обязан заполнять `code` (так бывает у
   * ошибок, собранных вручную в перехватчиках). Пустой тег хуже отсутствующего
   * — он выглядит как измеренное значение, поэтому здесь явное слово.
   */
  return error.type ?? 'unknown';
};
